import React, { useState, useEffect } from 'react';
import { onMaterialsChange } from '../../firebase/materials';
import { onFurnitureChange } from '../../firebase/furniture';
import { onDecorativeImagesChange } from '../../firebase/decorativeImages';
import { MaterialLibrary } from './MaterialComponents';
import { FurnitureLibrary } from './FurnitureComponents';
import { DecorativeImageLibrary } from './DecorativeImageComponents';
import { X, Palette, Armchair, ImageIcon } from 'lucide-react';

export const CombinedLibrary = ({ isOpen, onClose, ownerId, onSelectItem, canManage, initialTab = 'materials', onEditItem, purpose }) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const [materials, setMaterials] = useState([]);
    const [furniture, setFurniture] = useState([]);
    const [decorativeImages, setDecorativeImages] = useState([]);

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    useEffect(() => {
        if (!isOpen || !ownerId) return;
        const unsubMaterials = onMaterialsChange(ownerId, setMaterials);
        const unsubFurniture = onFurnitureChange(ownerId, setFurniture);
        const unsubDecorativeImages = onDecorativeImagesChange(ownerId, setDecorativeImages);

        return () => {
            unsubMaterials();
            unsubFurniture();
            unsubDecorativeImages();
        };
    }, [isOpen, ownerId]);

    const handleItemSelect = (item, type) => {
        onSelectItem(item, type);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[70] p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-4xl relative h-full max-h-[80vh] flex flex-col">
                <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors z-20">
                    <X className="h-5 w-5" />
                </button>
                <div className="flex-shrink-0">
                    <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">Library</h2>
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                            <button
                                onClick={() => setActiveTab('materials')}
                                className={`group inline-flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'materials'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <Palette className="h-5 w-5" />
                                <span>Materials</span>
                            </button>
                            <button
                                onClick={() => setActiveTab('furniture')}
                                className={`group inline-flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'furniture'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <Armchair className="h-5 w-5" />
                                <span>Furniture</span>
                            </button>
                             <button
                                onClick={() => setActiveTab('decorative')}
                                className={`group inline-flex items-center gap-2 py-3 px-1 border-b-2 font-medium text-sm ${
                                    activeTab === 'decorative'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                                }`}
                            >
                                <ImageIcon className="h-5 w-5" />
                                <span>Images</span>
                            </button>
                        </nav>
                    </div>
                </div>

                <div className="flex-grow min-h-0 pt-6 flex flex-col">
                    {activeTab === 'materials' && (
                        <MaterialLibrary
                            onSelectItem={(item) => handleItemSelect(item, 'material')}
                            ownerId={ownerId}
                            materials={materials}
                            canManage={canManage}
                            onEditItem={(item) => onEditItem(item, 'material')}
                            purpose={purpose}
                        />
                    )}
                    {activeTab === 'furniture' && (
                        <FurnitureLibrary
                            onSelectItem={(item) => handleItemSelect(item, 'furniture')}
                            ownerId={ownerId}
                            furniture={furniture}
                            canManage={canManage}
                            onEditItem={(item) => onEditItem(item, 'furniture')}
                            purpose={purpose}
                        />
                    )}
                    {activeTab === 'decorative' && (
                        <DecorativeImageLibrary
                            onSelectItem={(item) => handleItemSelect(item, 'decorative')}
                            ownerId={ownerId}
                            images={decorativeImages}
                            canManage={canManage}
                            onEditItem={(item) => onEditItem(item, 'decorative')}
                            purpose={purpose}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
